import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { isSuperAdmin } from '@/utils/adminUtils';
import AdminPreviewPanel from './AdminPreviewPanel';
import OrganizationAnalytics from './OrganizationAnalytics';
import { Shield, Eye, AlertCircle } from 'lucide-react';
import { Navigate } from 'react-router-dom';

const AdminSupportDashboard: React.FC = () => {
  const { user } = useAuth();

  // Check if user is super admin
  if (!user || !isSuperAdmin(user.email)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-primary-600" />
            <h1 className="text-3xl font-bold text-gray-900">Admin Support Dashboard</h1>
          </div>
          <p className="text-gray-600">
            Preview organizations and provide customer support
          </p>
        </div>

        {/* Admin Notice */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900 mb-1">Super Admin Access</h3>
              <p className="text-sm text-blue-700">
                You have super admin privileges. Use preview mode to view the application exactly as customers see it.
                All preview actions are logged for audit purposes.
              </p>
            </div>
          </div>
        </div>

        {/* Preview Panel */}
        <div className="mb-8">
          <AdminPreviewPanel />
        </div>

        {/* Organization Analytics */}
        <div className="mb-8">
          <OrganizationAnalytics />
        </div>

        {/* Future Sections Placeholder */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900">Health Monitoring</h2>
            </div>
            <p className="text-sm text-gray-500">
              Coming soon: Monitor organization health scores and identify at-risk customers proactively.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSupportDashboard;

